/**
 * Require terms checkbox before checkout (cart page + drawer).
 * Persistent document listeners so AJAX cart re-renders cannot drop the gate.
 */
(function initCartTermsGate() {
  if (window.__cartTermsGate) return;
  window.__cartTermsGate = true;

  const CONFIRM = () =>
    theme?.strings?.cartTermsConfirmation || 'Please agree to the terms before checking out.';

  function termsBoxes() {
    return Array.from(document.querySelectorAll('.js-cart-terms-checkbox'));
  }

  function relevantCheckbox(fromEl) {
    const boxes = termsBoxes();
    if (!boxes.length) return null;

    const formId =
      fromEl?.getAttribute?.('form') ||
      fromEl?.closest?.('form')?.id ||
      null;

    if (formId) {
      const byForm = boxes.find((box) => box.getAttribute('form') === formId);
      if (byForm) return byForm;
    }

    const root = fromEl?.closest?.('cart-drawer, #cart-drawer, #cart, .cc-main-cart, .cart');
    if (root) {
      const inRoot = root.querySelector('.js-cart-terms-checkbox');
      if (inRoot) return inRoot;
    }

    // Visible checkbox near the control (drawer summary / cart summary)
    const summary = fromEl?.closest?.('.cart-drawer__summary, .cart__summary, #cart-summary');
    if (summary) {
      const inSummary = summary.querySelector('.js-cart-terms-checkbox');
      if (inSummary) return inSummary;
    }

    return boxes[0];
  }

  function checkoutButtons() {
    return Array.from(
      document.querySelectorAll('.js-cart-checkout, button[name="checkout"], input[name="checkout"]')
    );
  }

  function lockButton(btn) {
    btn.classList.add('cart-checkout--terms-blocked');
    btn.setAttribute('aria-disabled', 'true');
    btn.disabled = true;
    btn.dataset.termsDisabled = '1';
    if (btn.tagName === 'BUTTON') {
      if (btn.getAttribute('type') === 'submit' || btn.dataset.termsWasSubmit === '1') {
        btn.dataset.termsWasSubmit = '1';
        btn.setAttribute('type', 'button');
      }
    }
  }

  function unlockButton(btn) {
    btn.classList.remove('cart-checkout--terms-blocked');
    btn.removeAttribute('aria-disabled');
    if (btn.dataset.termsDisabled === '1') {
      btn.disabled = false;
      delete btn.dataset.termsDisabled;
    }
    if (btn.dataset.termsWasSubmit === '1') {
      btn.setAttribute('type', 'submit');
      delete btn.dataset.termsWasSubmit;
    }
  }

  function syncCheckoutButtons() {
    checkoutButtons().forEach((btn) => {
      if (btn.classList.contains('cart-checkout--blocked')) return;

      const box = relevantCheckbox(btn);
      if (!box) {
        // Don't unlock if a terms box exists elsewhere on the page — avoids
        // brief AJAX gaps re-enabling checkout.
        if (!termsBoxes().length) unlockButton(btn);
        return;
      }

      if (box.checked) unlockButton(btn);
      else lockButton(btn);
    });
  }

  function block(evt, box) {
    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();
    syncCheckoutButtons();
    // eslint-disable-next-line no-alert
    alert(CONFIRM());
    try {
      box?.focus?.({ preventScroll: false });
    } catch (e) {
      /* ignore */
    }
  }

  function checkoutControlFromEvent(evt) {
    return evt.target.closest?.(
      '.js-cart-checkout, button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href*="/checkout"]'
    );
  }

  function handleCheckoutAttempt(evt) {
    const btn = checkoutControlFromEvent(evt);
    if (!btn) return;
    if (btn.classList.contains('cart-checkout--blocked')) return;

    const box = relevantCheckbox(btn);
    if (box && !box.checked) block(evt, box);
  }

  // Capture early — before Bird / other handlers
  document.addEventListener('pointerdown', handleCheckoutAttempt, true);
  document.addEventListener('click', handleCheckoutAttempt, true);

  document.addEventListener(
    'submit',
    (evt) => {
      const form = evt.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.id !== 'cart' && form.id !== 'cart-drawer-form') return;

      const box =
        document.querySelector(`.js-cart-terms-checkbox[form="${form.id}"]`) ||
        form.querySelector('.js-cart-terms-checkbox') ||
        relevantCheckbox(form);

      if (box && !box.checked) block(evt, box);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (evt) => {
      if (evt.key !== 'Enter' && evt.key !== ' ') return;
      handleCheckoutAttempt(evt);
    },
    true
  );

  document.addEventListener(
    'change',
    (evt) => {
      if (evt.target?.matches?.('.js-cart-terms-checkbox')) syncCheckoutButtons();
    },
    true
  );

  // Re-lock if something (e.g. Bird) strips disabled / aria-disabled
  const mo = new MutationObserver((mutations) => {
    let needsSync = false;
    for (const m of mutations) {
      if (m.type === 'childList') {
        needsSync = [...m.addedNodes, ...m.removedNodes].some(
          (n) =>
            n.nodeType === 1 &&
            (n.matches?.('.js-cart-checkout, cart-terms, .js-cart-terms-checkbox, .cart-drawer__summary, .cart__summary') ||
              n.querySelector?.('.js-cart-checkout, cart-terms, .js-cart-terms-checkbox'))
        );
      } else if (m.type === 'attributes' && m.target.matches?.('.js-cart-checkout, button[name="checkout"]')) {
        const btn = m.target;
        const box = relevantCheckbox(btn);
        if (box && !box.checked && (!btn.disabled || btn.getAttribute('aria-disabled') !== 'true')) {
          needsSync = true;
        }
      }
      if (needsSync) break;
    }
    if (needsSync) syncCheckoutButtons();
  });

  mo.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled', 'type', 'class']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncCheckoutButtons);
  } else {
    syncCheckoutButtons();
  }
})();

if (!customElements.get('cart-terms')) {
  class CartTerms extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('.js-cart-terms-checkbox');
      if (this.checkbox) this.checkbox.setAttribute('required', '');
    }
  }
  customElements.define('cart-terms', CartTerms);
}
